/**
 * LONDA Product UX — revision 2, 2026-09-24.
 * Display-only product data. An explicit customer click adds exactly one unit.
 * No Admin API, product/price writes, fake wishlist storage, or automatic cart retries.
 */
(() => {
  'use strict';
  if (customElements.get('londa-product')) return;
  class LondaProduct extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      if (!this.querySelector('[data-lpd-data]')) {
        this.startObserver = new MutationObserver(() => {
          if (this.querySelector('[data-lpd-data]')) { this.startObserver.disconnect(); this.initialize(); }
        });
        this.startObserver.observe(this, {childList:true,subtree:true});
        return;
      }
      this.initialize();
    }
    initialize() {
      if (this.ready) return;
      try { this.data = JSON.parse(this.querySelector('[data-lpd-data]').textContent); }
      catch (error) { console.error('LONDA: product data unreadable; native form retained.',error); return; }
      this.form = this.querySelector('[data-lpd-form]');
      this.variantInput = this.querySelector('[data-lpd-id]');
      if (!this.form || !this.variantInput) return;
      this.ready = true;
      this.controller = new AbortController(); this.signal = this.controller.signal;
      this.variants = new Map(this.data.variants.map(v=>[String(v.id),v]));
      this.quantity = this.querySelector('[data-lpd-quantity]');
      this.purchaseHome = this.querySelector('[data-lpd-purchase-home]');
      this.confirm = this.querySelector('[data-lpd-confirm]');
      this.lightbox = this.querySelector('[data-lpd-lightbox]');
      this.track = this.querySelector('[data-lpd-track]');
      this.mediaNodes = [...this.querySelectorAll('[data-lpd-media]')];
      this.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.busy = false; this.mediaIndex = 0; this.galleryVisible = true;
      this.classList.add('lpd-enhanced'); document.documentElement.classList.add('londa-pdp-page');
      this.on(this,'click',e=>this.handleClick(e));
      this.on(this,'keydown',e=>this.handleKey(e));
      this.on(this.form,'submit',e=>this.submit(e));
      this.on(this.variantInput,'change',()=>{ if(!this.syncing) this.selectVariant(this.variantInput.value,{notify:false}); });
      this.on(window,'popstate',()=>this.selectVariant(new URL(location.href).searchParams.get('variant') || this.data.initialVariant,{history:false}));
      this.on(window,'resize',()=>{this.updateHeaderOffset(); this.goToMedia(this.mediaIndex,false);});
      this.on(document,'visibilitychange',()=>this.updateMediaPlayback());
      this.on(this.reduceMotion,'change',()=>{if(this.reduceMotion.matches)this.pauseAllMedia();this.updateMediaPlayback();});
      this.querySelectorAll('.lpd-dialog').forEach(dialog=>{
        this.on(dialog,'click',e=>{
          if(e.target!==dialog)return; const r=dialog.getBoundingClientRect();
          if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)dialog.close();
        });
        this.on(dialog,'close',()=>this.onDialogClose(dialog));
      });
      const firstIsVideo = ['video','external_video'].includes(this.data.media[0]?.type);
      const id = new URL(location.href).searchParams.get('variant') || this.data.initialVariant;
this.selectVariant(id,{history:false,notify:false,moveImage:false});
      this.setupGallery(); this.updateHeaderOffset(); this.setupWishlist();
      if(this.reduceMotion.matches)this.pauseAllMedia();
      this.updateMediaPlayback();
    }
    on(target,type,listener,options={}) { if(target)target.addEventListener(type,listener,{...options,signal:this.signal}); }
    text(selector,value) { this.querySelectorAll(selector).forEach(el=>{const t=String(value??'');if(el.textContent!==t)el.textContent=t;}); }
    attr(el,name,value) { if(el && el.getAttribute(name)!==String(value))el.setAttribute(name,String(value)); }
    actionStatus(message) { this.text('[data-lpd-action-status]',message);this.querySelector('[data-lpd-action-status]').hidden=!message; }
    handleClick(event) {
      const button=event.target.closest('button,a');
      if(!button || !this.contains(button) || button.hasAttribute('data-lpd-native-wishlist')) return;
      if(button.hasAttribute('data-lpd-close')){button.closest('dialog')?.close();return;}
      if(button.hasAttribute('data-lpd-add')){
        event.preventDefault(); if(this.busy||button.disabled)return;
        this.selectVariant(button.dataset.lpdAdd);this.addButton=button;
        if(this.form.requestSubmit)this.form.requestSubmit();else this.form.dispatchEvent(new Event('submit',{bubbles:true,cancelable:true}));return;
      }
      if(button.hasAttribute('data-lpd-select')){this.selectVariant(button.dataset.lpdSelect);return;}
      if(button.hasAttribute('data-lpd-wishlist')){event.preventDefault();this.toggleWishlist(button);return;}
      if(button.hasAttribute('data-lpd-open-size')){event.preventDefault();this.openSize(button);return;}
      if(button.hasAttribute('data-lpd-copy-code')){this.copyCode(button);return;}
      if(button.hasAttribute('data-lpd-jump-options')){event.preventDefault();this.jumpTo(this.purchaseHome);return;}
      if(button.hasAttribute('data-lpd-option-card')){
        event.preventDefault();
        const value=button.dataset.lpdOptionCard;
        const matching=this.data.variants.filter(v=>v.options[this.data.groupIndex]===value);
        const variant=matching.find(v=>v.available)||matching[0];
        if(variant)this.selectVariant(variant.id);
        const group=[...this.querySelectorAll('[data-lpd-group]')].find(g=>g.dataset.lpdGroup===value);
        this.jumpTo(group||this.purchaseHome);return;
      }
      if(button.hasAttribute('data-lpd-goto')){this.goToMedia(Number(button.dataset.lpdGoto));return;}
      if(button.hasAttribute('data-lpd-prev')){this.goToMedia(this.mediaIndex-1);return;}
      if(button.hasAttribute('data-lpd-next')){this.goToMedia(this.mediaIndex+1);return;}
      if(button.hasAttribute('data-lpd-show-media')){
        const i=this.data.media.findIndex(m=>String(m.id)===button.dataset.lpdShowMedia);
        if(i>=0)this.goToMedia(i);return;
      }
      if(button.hasAttribute('data-lpd-zoom')){this.openZoom(Number(button.dataset.lpdZoom),button);return;}
      if(button.hasAttribute('data-lpd-zoom-prev')){this.stepZoom(-1);return;}
      if(button.hasAttribute('data-lpd-zoom-next')){this.stepZoom(1);return;}
      if(button.hasAttribute('data-lpd-share'))this.share();
    }
    jumpTo(element) {
      if(!element)return;
      element.scrollIntoView({block:'start',behavior:this.reduceMotion.matches?'auto':'smooth'});
      if(element===this.purchaseHome)element.focus({preventScroll:true});
    }
    handleKey(event) {
      if(event.target===this.track && ['ArrowLeft','ArrowRight'].includes(event.key)){
        event.preventDefault();this.goToMedia(this.mediaIndex+(event.key==='ArrowRight'?1:-1));
      }else if(this.lightbox?.open && ['ArrowLeft','ArrowRight'].includes(event.key)){
        event.preventDefault();this.stepZoom(event.key==='ArrowRight'?1:-1);
      }
    }
    selectVariant(id,{history=true,notify=true,moveImage=true}={}) {
      const v=this.variants.get(String(id))||this.variants.get(String(this.data.initialVariant)); if(!v)return;
      this.selected=v;this.variantInput.value=String(v.id);this.dataset.variantId=String(v.id);
      this.quantity.value='1';
      this.querySelectorAll('[data-lpd-select]').forEach(b=>this.attr(b,'aria-pressed',b.dataset.lpdSelect===String(v.id)));
      this.querySelectorAll('[data-lpd-option-card]').forEach(b=>this.attr(b,'aria-current',b.dataset.lpdOptionCard===v.options[this.data.groupIndex]));
      this.text('[data-lpd-price]',v.priceText);this.text('[data-lpd-compare]',v.compareText||'');
      this.text('[data-lpd-sale]',v.discount?v.discount+'% OFF':'');
      this.querySelectorAll('[data-lpd-compare],[data-lpd-sale]').forEach(el=>{el.hidden=!v.compareText;});
      this.text('[data-lpd-sku]',v.sku||'');this.querySelector('[data-lpd-sku-wrap]').hidden=!v.sku;
      this.text('[data-lpd-selection]',this.data.hasOnlyDefaultVariant?'':`${this.data.labels.selected}: ${v.options.join(' / ')}`);
      const nativeAdd=this.querySelector('.lpd-native-add');if(nativeAdd)nativeAdd.disabled=!v.available;
      if(moveImage && v.mediaId!=null){const i=this.data.media.findIndex(m=>String(m.id)===String(v.mediaId));if(i>=0)this.goToMedia(i,false);}
      if(history){try{const u=new URL(location.href);u.searchParams.set('variant',String(v.id));window.history.replaceState(window.history.state,'',u);}catch(_){}}
      if(notify){
        this.syncing=true;this.variantInput.dispatchEvent(new Event('change',{bubbles:true}));this.syncing=false;
        this.dispatchEvent(new CustomEvent('variant:change',{bubbles:true,detail:{variant:v,productId:this.data.productId}}));
        this.dispatchEvent(new CustomEvent('londa:variant-change',{bubbles:true,detail:{variant:v}}));
      }
      const paidy=this.querySelector('[data-lpd-paidy]');
      if(paidy){this.attr(paidy,'data-amount',(v.price/100).toFixed(2));if(typeof window._paidy==='function')window._paidy('pm:refresh');}
      this.syncWishlistState();
    }
    openSize(opener) {
      const host=this.querySelector('[data-londa-size-guide]');
      const root=host && [...host.querySelectorAll('*')].find(el=>el.shadowRoot)?.shadowRoot;
      const trigger=root?.querySelector('.trigger');
      if(!trigger){this.actionStatus(this.data.labels.sizeUnavailable);return;}
      this.actionStatus('');this.sizeGuideOpen=true;this.pauseAllMedia();
      const dialog=root.querySelector('dialog');
      if(dialog)this.on(dialog,'close',()=>{this.sizeGuideOpen=false;opener.focus({preventScroll:true});this.updateMediaPlayback();},{once:true});
      trigger.click();
    }
    setupWishlist() {
      // The app keeps its own storage/API. Bind only a real, identifiable existing wishlist button.
      const update=()=>{
        this.wishlistFrame=null;if(!this.isConnected)return;
        const scope=this.closest('.shopify-section')||this;
        const candidates=[...scope.querySelectorAll('button,a,[role="button"]')].filter(el=>{
          if(el.hasAttribute('data-lpd-wishlist')||el.closest('dialog')||el.classList.contains('lpd-text-button'))return false;
          const text=[el.textContent,el.getAttribute('aria-label'),el.getAttribute('title')].filter(Boolean).join(' ').trim();
          return text.length<220 && /(?:お気に入り|ウィッシュリスト).*(?:追加|登録|削除|解除)|(?:add(?:ed)?\s+(?:to\s+)?|remove\s+from\s+|in\s+)(?:the\s+)?(?:my\s+)?wish\s?list/i.test(text);
        });
        const preferred=candidates.find(el=>!el.disabled && this.contains(el))||candidates[0];
        if(preferred){
          this.nativeWishlist=preferred;
          candidates.forEach(el=>this.attr(el,'data-lpd-native-wishlist',''));
          this.syncWishlistState();
        }else this.nativeWishlist=null;
      };
      const schedule=()=>{if(!this.wishlistFrame)this.wishlistFrame=requestAnimationFrame(update);};
      this.wishlistObserver=new MutationObserver(schedule);
      this.wishlistObserver.observe(this.closest('.shopify-section')||this,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['aria-pressed','data-button-status','class','disabled']});
      update();
      const nativeLink=document.querySelector('a[aria-label="Wishlist page link"]');
      const target=this.querySelector('[data-lpd-wishlist-page]');
      if(nativeLink && target){try{const u=new URL(nativeLink.href,location.href);if(u.origin===location.origin)target.href=u.href;}catch(_){}}
    }
    readWishlistState() {
      const native=this.nativeWishlist;if(!native?.isConnected)return null;
      if(native.getAttribute('aria-pressed')==='true'||native.getAttribute('data-button-status')==='added')return true;
      const text=[native.textContent,native.getAttribute('aria-label'),native.getAttribute('title')].join(' ');
      if(/削除|解除|登録済|追加済|remove\s+from|added\s+to|saved\s+to/i.test(text))return true;
      if(native.getAttribute('aria-pressed')==='false'||/に追加|登録する|add\s+to/i.test(text))return false;
      return null;
    }
    syncWishlistState() {
      const state=this.readWishlistState();if(state===null)return;
      const top=this.querySelector('.lpd-wishlist-top');this.attr(top,'aria-pressed',state);
      this.text('[data-lpd-wishlist-label]',state?this.data.labels.wishlistAdded:this.data.labels.wishlistAdd);
      // Only mirror a variant's state when that variant is the one synchronized with the app.
      this.querySelectorAll('.lpd-row-wishlist').forEach(b=>{
        if(b.dataset.lpdWishlist===String(this.selected?.id))this.attr(b,'aria-pressed',state);
        else b.removeAttribute('aria-pressed');
      });
      if(this.wishlistPending && state!==this.wishlistBefore){this.wishlistPending=false;clearTimeout(this.wishlistTimeout);this.actionStatus('');}
    }
    toggleWishlist(button) {
      if(this.wishlistPending || this.busy)return;
      if(button.dataset.lpdWishlist)this.selectVariant(button.dataset.lpdWishlist,{moveImage:false});
      const native=this.nativeWishlist;
      if(!native?.isConnected || native.disabled){this.actionStatus(this.data.labels.wishlistUnavailable);return;}
      this.wishlistBefore=this.readWishlistState();this.wishlistPending=true;
      this.actionStatus(this.data.labels.wishlistWait);
      // Wait for variant-change listeners to finish, then invoke the app's own control once.
      requestAnimationFrame(()=>{
        if(!this.isConnected)return;
        try{native.click();this.syncWishlistState();}catch(_){this.wishlistPending=false;this.actionStatus(this.data.labels.wishlistUnavailable);}
        if(this.wishlistPending)this.wishlistTimeout=setTimeout(()=>{this.wishlistPending=false;this.actionStatus(this.data.labels.wishlistCheckList||this.data.labels.wishlistWait);},6000);
      });
    }
    async copyCode(button) {
      const code=button.dataset.lpdCopyCode;
      let copied=false;
      try{await navigator.clipboard.writeText(code);copied=true;}catch(_){
        const field=document.createElement('textarea');field.value=code;field.style.cssText='position:fixed;left:-9999px;top:0';document.body.append(field);field.select();
        try{copied=document.execCommand('copy');}catch(_){}field.remove();button.focus({preventScroll:true});
      }
      this.text('[data-lpd-coupon-status]',copied?this.data.labels.couponCopied:`${this.data.labels.copyCode}: ${code}`);
      this.querySelector('[data-lpd-coupon-status]').hidden=false;
    }
    updateHeaderOffset() {
      const header=document.querySelector('[data-section-type="header"],header');
      const height=header?Math.min(180,Math.max(56,header.getBoundingClientRect().height)):76;
      this.style.setProperty('--lpd-header-offset',`${height+20}px`);
    }
    setupGallery() {
      if(typeof IntersectionObserver==='function' && this.mediaNodes.length){
        this.galleryObserver=new IntersectionObserver(entries=>{
          for(const e of entries)if(e.isIntersecting && e.intersectionRatio>.6)this.markMedia(Number(e.target.dataset.lpdIndex));
        },{root:this.track,threshold:[.6,.85]});this.mediaNodes.forEach(n=>this.galleryObserver.observe(n));
        this.visibilityObserver=new IntersectionObserver(entries=>{this.galleryVisible=entries[0]?.isIntersecting!==false;this.updateMediaPlayback();},{threshold:.15});
        this.visibilityObserver.observe(this.track);
      }else this.on(this.track,'scroll',()=>this.markMedia(Math.round(this.track.scrollLeft/Math.max(1,this.track.clientWidth))),{passive:true});
      this.mediaNodes.forEach((node,index)=>{
        const video=node.querySelector('video');
        if(video){
          if(index===0){video.muted=true;video.defaultMuted=true;video.loop=true;video.playsInline=true;video.setAttribute('playsinline','');}
          this.on(video,'loadedmetadata',()=>this.updateMediaPlayback());
          // Native pause controls remain usable. Do not override an intentional manual pause.
          this.on(video,'pause',()=>{if(!this.automaticPause && index===this.mediaIndex && this.galleryVisible && !document.hidden)this.userPausedVideo=video;});
          this.on(video,'play',()=>{if(this.userPausedVideo===video)this.userPausedVideo=null;});
        }
        node.querySelectorAll('iframe[data-lpd-external]').forEach(frame=>this.on(frame,'load',()=>this.updateMediaPlayback()));
      });
    }
    pauseVideo(video) {
      if(video.paused)return;
      this.automaticPause=true;video.pause();setTimeout(()=>{this.automaticPause=false;},0);
    }
    frameCommand(frame,play) {
      try{
        const url=new URL(frame.src,location.href),host=url.hostname;
        if(host==='www.youtube.com'||host==='www.youtube-nocookie.com'){
          if(play)frame.contentWindow?.postMessage(JSON.stringify({event:'command',func:'mute',args:[]}),url.origin);
          frame.contentWindow?.postMessage(JSON.stringify({event:'command',func:play?'playVideo':'pauseVideo',args:[]}),url.origin);
        }else if(host==='player.vimeo.com'){
          if(play)frame.contentWindow?.postMessage({method:'setVolume',value:0},url.origin);
          frame.contentWindow?.postMessage({method:play?'play':'pause'},url.origin);
        }
      }catch(_){}
    }
    pauseAllMedia() {(this.mediaNodes||[]).forEach(node=>{node.querySelectorAll('video').forEach(v=>this.pauseVideo(v));node.querySelectorAll('iframe').forEach(f=>this.frameCommand(f,false));});}
    updateMediaPlayback() {
      const canAutoPlay=this.mediaIndex===0 && this.galleryVisible && !document.hidden && !this.reduceMotion.matches && !this.sizeGuideOpen && !this.querySelector('dialog[open]');
      this.mediaNodes.forEach((node,index)=>{
        node.querySelectorAll('video').forEach(video=>{
          if(index!==this.mediaIndex || !this.galleryVisible || document.hidden || this.sizeGuideOpen || this.querySelector('dialog[open]')){this.pauseVideo(video);return;}
          if(index!==0)return;
          if(this.reduceMotion.matches){video.autoplay=false;return;}
          if(canAutoPlay && this.userPausedVideo!==video && video.paused){
            video.muted=true;video.defaultMuted=true;video.autoplay=true;video.playsInline=true;
            // Rejection (power-saving/browser preferences) is not retried; native controls remain.
            if(video.dataset.lpdAutoplayBlocked)return;
            const promise=video.play();if(promise?.catch)promise.catch(()=>{video.dataset.lpdAutoplayBlocked='true';video.controls=true;});
          }
        });
        node.querySelectorAll('iframe[data-lpd-external]').forEach(frame=>{
          const play=index===0 && canAutoPlay;
          if(play && !frame.dataset.lpdAutoConfigured){
            try{const u=new URL(frame.src);u.searchParams.set('autoplay','1');u.searchParams.set(frame.dataset.lpdExternal==='vimeo'?'muted':'mute','1');frame.dataset.lpdAutoConfigured='true';frame.src=u.href;}catch(_){}
          }else this.frameCommand(frame,play);
        });
      });
    }
    markMedia(index) {
      if(index<0||index>=this.mediaNodes.length)return;
      this.mediaIndex=index;this.text('[data-lpd-counter]',`${index+1} / ${this.mediaNodes.length}`);
      this.querySelectorAll('[data-lpd-goto]').forEach(b=>this.attr(b,'aria-current',Number(b.dataset.lpdGoto)===index));
      const selected=this.querySelector(`[data-lpd-goto="${index}"]`),thumbs=this.querySelector('[data-lpd-thumbnails]');
      if(thumbs&&selected){const left=selected.offsetLeft-thumbs.offsetLeft;if(left<thumbs.scrollLeft || left+selected.offsetWidth>thumbs.scrollLeft+thumbs.clientWidth)thumbs.scrollTo({left:Math.max(0,left-thumbs.clientWidth/2+selected.offsetWidth/2),behavior:'auto'});}
      this.updateMediaPlayback();
    }
    goToMedia(index,smooth=true) {
      if(!this.mediaNodes.length)return;index=(index+this.mediaNodes.length)%this.mediaNodes.length;
      this.track.scrollTo({left:this.track.clientWidth*index,behavior:smooth&&!this.reduceMotion.matches?'smooth':'auto'});this.markMedia(index);
    }
    showDialog(dialog) {
      if(!dialog || dialog.open)return;
      if(typeof dialog.showModal!=='function'){
        if(dialog===this.confirm)window.location.assign(this.data.cartUrl);
        return;
      }
      if(!this.pageLocked){this.previousOverflow=document.documentElement.style.overflow;document.documentElement.style.overflow='hidden';this.pageLocked=true;}
      dialog.showModal();this.pauseAllMedia();dialog.querySelector('[data-lpd-close]')?.focus({preventScroll:true});
    }
    onDialogClose(dialog) {
      if(!this.querySelector('.lpd-dialog[open]'))this.unlockPage();
      const focus=dialog===this.lightbox?this.zoomOpener:this.addButton;
      if(focus?.isConnected&&!this.querySelector('.lpd-dialog[open]'))focus.focus({preventScroll:true});
      this.updateMediaPlayback();
    }
    unlockPage(){if(this.pageLocked){if(document.documentElement)document.documentElement.style.overflow=this.previousOverflow||'';this.pageLocked=false;}}
    openZoom(index,opener){if(this.data.media[index]?.type!=='image')return;this.zoomIndex=index;this.zoomOpener=opener;this.renderZoom();this.showDialog(this.lightbox);}
    stepZoom(direction){const images=this.data.media.map((m,i)=>m.type==='image'?i:-1).filter(i=>i>=0);if(!images.length)return;this.zoomIndex=images[(images.indexOf(this.zoomIndex)+direction+images.length)%images.length];this.renderZoom();}
    renderZoom(){const media=this.data.media[this.zoomIndex],image=this.querySelector('[data-lpd-zoom-image]');image.src=media.src;image.alt=media.alt||this.data.title;this.text('[data-lpd-zoom-counter]',`${this.zoomIndex+1} / ${this.data.media.length}`);}
    setBusy(busy){
      this.busy=busy;this.attr(this.form,'aria-busy',busy);
      this.querySelectorAll('[data-lpd-add]').forEach(b=>{const v=this.variants.get(b.dataset.lpdAdd);b.disabled=busy||!v?.available;const t=busy&&b===this.addButton?this.data.labels.adding:v?.available?this.data.labels.add:this.data.labels.soldOut;if(b.textContent!==t)b.textContent=t;});
      this.querySelectorAll('[data-lpd-select],[data-lpd-wishlist]').forEach(b=>{b.disabled=busy;});
    }
    showError(message){const error=this.querySelector('[data-lpd-error]');this.text('[data-lpd-error-text]',message||this.data.labels.error);error.hidden=false;error.focus({preventScroll:true});error.scrollIntoView({block:'nearest'});}
    async submit(event){
      event.preventDefault();if(this.busy)return;
      const variant=this.variants.get(this.variantInput.value);
      if(!variant?.available){this.showError(this.data.labels.soldOut);return;}
      const quantity=1;
      if(quantity<(variant.min||1)||(variant.max!=null&&quantity>variant.max)||(quantity-(variant.min||1))%(variant.step||1)!==0){this.showError(this.data.labels.singleItemError);return;}
      if(!this.form.reportValidity())return;
      this.querySelector('[data-lpd-error]').hidden=true;this.setBusy(true);
      const body=new FormData(this.form);body.set('id',String(variant.id));body.set('quantity','1');
      let added=false;
      try{
        const response=await fetch(this.data.addUrl,{method:'POST',body,headers:{Accept:'application/json'},credentials:'same-origin'});
        let result;
        try{result=await response.json();}catch(_){if(response.ok){added=true;result={};}else throw new Error(this.data.labels.error);}
        if(!response.ok){this.showError(typeof result.description==='string'?result.description:typeof result.message==='string'?result.message:this.data.labels.error);return;}
        added=true;
        const info={button:this.addButton,product:this,data:{id:variant.id,quantity},response:result};
        document.dispatchEvent(new CustomEvent('theme:cart::added',{detail:info}));
        this.dispatchEvent(new CustomEvent('londa:cart-added',{bubbles:true,detail:info}));
        this.showConfirmation(variant,quantity);
        this.refreshCart().catch(e=>console.warn('LONDA: item added; count refresh unavailable.',e));
      }catch(error){console.error('LONDA cart request:',error);if(added)this.showConfirmation(variant,quantity);else this.showError(this.data.labels.networkError);}
      finally{this.setBusy(false);}
    }
    showConfirmation(variant,quantity){
      this.text('[data-lpd-confirm-variant]',this.data.hasOnlyDefaultVariant?'':variant.options.join(' / '));
      this.text('[data-lpd-confirm-quantity]',`${this.data.labels.quantity}: ${quantity}`);
      const image=this.querySelector('[data-lpd-confirm-image]'),src=variant.image||this.data.media.find(m=>m.type==='image')?.src;
      image.hidden=!src;if(src){image.src=src;image.alt=this.data.title;}
      this.showDialog(this.confirm);
    }
    async refreshCart(){
      const response=await fetch(`${this.data.cartUrl}.js`,{headers:{Accept:'application/json'},credentials:'same-origin',cache:'no-store'});
      if(!response.ok)throw new Error('Cart refresh failed');const cart=await response.json();const controller=window.theme?.Cart;
      if(controller){try{if(typeof controller.setCurrentData==='function')controller.setCurrentData(cart);else controller.currentData=cart;}catch(_){controller.currentData=cart;controller.hasData=true;}if(typeof controller.updateCart==='function')Promise.resolve(controller.updateCart()).catch(()=>{});}
      const desktopText=window.theme?.strings?.header?.cart_count_desktop;
      document.querySelectorAll('[data-js-cart-count-desktop]').forEach(el=>{el.setAttribute('data-js-cart-count-desktop',cart.item_count);el.textContent=desktopText?desktopText.replace('{{ count }}',cart.item_count):String(cart.item_count);});
      document.querySelectorAll('[data-js-cart-count-mobile]').forEach(el=>{el.setAttribute('data-js-cart-count-mobile',cart.item_count);el.textContent=String(cart.item_count);});
      document.dispatchEvent(new CustomEvent('cart:updated',{detail:{cart,source:'londa-product'}}));
    }
    async share(){
      const url=new URL(this.data.productUrl,location.origin);if(this.selected&&!this.data.hasOnlyDefaultVariant)url.searchParams.set('variant',this.selected.id);
      try{if(navigator.share){await navigator.share({title:this.data.title,url:url.href});return;}await navigator.clipboard.writeText(url.href);this.text('[data-lpd-share-status]',this.data.labels.copied);}
      catch(error){if(error.name==='AbortError')return;this.text('[data-lpd-share-status]',`${this.data.labels.copyFallback} ${url.href}`);}
      this.querySelector('[data-lpd-share-status]').hidden=false;
    }
    disconnectedCallback(){
      this.pauseAllMedia();this.controller?.abort();this.startObserver?.disconnect();this.galleryObserver?.disconnect();this.visibilityObserver?.disconnect();this.wishlistObserver?.disconnect();
      if(this.wishlistFrame)cancelAnimationFrame(this.wishlistFrame);clearTimeout(this.wishlistTimeout);this.unlockPage();this.ready=false;
    }
  }
  customElements.define('londa-product',LondaProduct);
})();
